import React, { useState, useRef, useEffect } from 'react';
import { Play, Pause, BookOpen, Users, Headphones, ShoppingBag } from 'lucide-react';
import Icon from '../../../components/AppIcon';

const AUDIO_BASE = 'https://assets.calltify.in/website';

const useCases = [
  {
    id: 1,
    icon: BookOpen,
    title: 'Bookings & Appointments',
    description: 'AI schedules and confirms bookings instantly',
    side: 'left',
    audio: `${AUDIO_BASE}/Doctor_Appointment_Booking_Call_V2.wav`,
  },
  {
    id: 2,
    icon: Users,
    title: 'Lead Qualification',
    description: 'Qualify leads with intelligent conversations',
    side: 'left',
    audio: `${AUDIO_BASE}/EdTech_Lead_Qualification_Call_v2.wav`,
  },
  {
    id: 3,
    icon: Headphones,
    title: 'Customer Support',
    description: 'Resolve queries 24/7 without human agents',
    side: 'right',
    audio: `${AUDIO_BASE}/damaged_product.mp3`,
  },
  {
    id: 4,
    icon: ShoppingBag,
    title: 'Product Recommendation',
    description: 'Guide customers to the right products',
    side: 'right',
    audio: `${AUDIO_BASE}/EMI_Customer_V2.wav`,
  },
];

const UseCaseCard = ({ useCase, isActive, isPlaying, onClick }) => {
  const { icon: Icon, title, description } = useCase;
  const PlayPauseIcon = isActive && isPlaying ? Pause : Play;
  return (
    <div
      onClick={() => onClick(useCase?.id)}
      className="flex items-start gap-3 p-4 rounded-2xl cursor-pointer transition-all duration-250"
      style={{
        background: isActive ? '#fff' : 'rgba(255,255,255,0.6)',
        border: isActive ? '1.5px solid #4F6BFF' : '1.5px solid #EAECEF',
        boxShadow: isActive ? '0 8px 32px rgba(79,107,255,0.15)' : '0 2px 8px rgba(0,0,0,0.04)',
        transform: isActive ? 'translateY(-2px)' : 'translateY(0)',
      }}
      onMouseEnter={e => { if (!isActive) { e.currentTarget.style.transform = 'translateY(-2px)'; e.currentTarget.style.boxShadow = '0 6px 20px rgba(0,0,0,0.08)'; } }}
      onMouseLeave={e => { if (!isActive) { e.currentTarget.style.transform = 'translateY(0)'; e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.04)'; } }}
    >
      <div
        className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
        style={{ background: isActive ? 'rgba(79,107,255,0.1)' : 'rgba(79,107,255,0.06)', border: '1px solid rgba(79,107,255,0.15)' }}
      >
        <Icon size={18} color="#4F6BFF" />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold mb-0.5" style={{ color: '#111827' }}>{title}</p>
        <p className="text-xs leading-relaxed" style={{ color: '#6B7280' }}>{description}</p>
      </div>
      <button
        className="w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 transition-all duration-250"
        style={{
          background: isActive ? '#4F6BFF' : 'rgba(79,107,255,0.1)',
          boxShadow: isActive ? '0 0 0 4px rgba(79,107,255,0.2)' : 'none',
          animation: isActive && isPlaying ? 'pulse 2s ease-in-out infinite' : 'none',
        }}
      >
        <PlayPauseIcon size={12} color={isActive ? '#fff' : '#4F6BFF'} fill={isActive ? '#fff' : '#4F6BFF'} />
      </button>
    </div>
  );
};

const AudioDemoSection = () => {
  const [activeCard, setActiveCard] = useState(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef(null);

  const handleCardClick = (id) => {
    if (id === activeCard) {
      if (isPlaying) {
        audioRef.current?.pause();
        setIsPlaying(false);
      } else {
        audioRef.current?.play();
        setIsPlaying(true);
        window.dataLayer?.push({ event: 'play_sample_audio', use_case: id });
      }
    } else {
      setActiveCard(id);
      setIsPlaying(true);
      window.dataLayer?.push({ event: 'play_sample_audio', use_case: id });
    }
  };

  useEffect(() => {
    if (!activeCard) return;
    const audio = audioRef.current;
    if (!audio) return;
    const src = useCases.find(u => u.id === activeCard)?.audio;
    if (src) {
      audio.src = src;
      audio.play().catch(() => setIsPlaying(false));
    }
  }, [activeCard]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio) return;
    const onEnded = () => setIsPlaying(false);
    audio.addEventListener('ended', onEnded);
    return () => audio.removeEventListener('ended', onEnded);
  }, []);

  const handleOrbClick = () => {
    if (isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
    } else {
      audioRef.current?.play();
      setIsPlaying(true);
      window.dataLayer?.push({ event: 'play_sample_audio', use_case: activeCard });
    }
  };

  const leftCards = useCases?.filter(c => c?.side === 'left');
  const rightCards = useCases?.filter(c => c?.side === 'right');

  return (
    <section className="pt-6 pb-2 lg:py-6 px-6 lg:px-8" style={{ background: '#FAFBFF' }}>
      <style>{`
        @keyframes pulse {
          0%, 100% { box-shadow: 0 0 0 4px rgba(79,107,255,0.2); }
          50% { box-shadow: 0 0 0 8px rgba(79,107,255,0.1); }
        }
        @keyframes orbPulse {
          0%, 100% { box-shadow: 0 0 40px 10px rgba(79,107,255,0.3), 0 0 80px 20px rgba(110,92,255,0.15); }
          50% { box-shadow: 0 0 60px 20px rgba(79,107,255,0.4), 0 0 120px 40px rgba(110,92,255,0.2); }
        }
        @keyframes orbRotate {
          from { transform: rotate(0deg); }
          to { transform: rotate(360deg); }
        }
      `}</style>
      <div className="mx-auto" style={{ maxWidth: '1280px' }}>
        {/* Header */}
        <div className="text-center mb-8">
          <h2 className="text-4xl lg:text-5xl font-bold mb-4 tracking-tight" style={{ color: '#111827' }}>
            Hear How Our AI Handles
            <br />
            <span style={{ background: 'linear-gradient(135deg, #5A7BFF 0%, #6E5CFF 100%)', WebkitBackgroundClip: 'text', WebkitTextFillColor: 'transparent', backgroundClip: 'text' }}>
              Real Customer Calls
            </span>
          </h2>
          <p className="text-lg" style={{ color: '#6B7280', maxWidth: '520px', margin: '0 auto' }}>
            Tap on a use case to listen to a real AI Agent–Customer conversation
          </p>
        </div>

        {/* Three-column layout */}
        <div className="flex flex-col lg:flex-row items-center gap-8 lg:gap-12">
          {/* Left Cards */}
          <div className="w-full lg:w-[30%] flex flex-col gap-4">
            {leftCards?.map(uc => (
              <UseCaseCard key={uc?.id} useCase={uc} isActive={activeCard === uc?.id} isPlaying={isPlaying} onClick={handleCardClick} />
            ))}
          </div>

          {/* Center Orb */}
          <div className="flex-shrink-0 flex items-center justify-center w-full lg:w-[40%] py-12 lg:py-0">
            <div className="relative flex items-center justify-center">
              {/* Outer glow ring */}
              <div
                className="absolute w-64 h-64 rounded-full"
                style={{
                  background: 'radial-gradient(circle, rgba(79,107,255,0.08) 0%, transparent 70%)',
                  animation: isPlaying ? 'orbPulse 3s ease-in-out infinite' : 'none',
                }}
              />
              {/* Rotating ring */}
              <div
                className="absolute w-52 h-52 rounded-full"
                style={{
                  border: '1px dashed rgba(79,107,255,0.3)',
                  animation: 'orbRotate 12s linear infinite',
                }}
              />
              {/* Main orb */}
              <div
                className="relative w-40 h-40 rounded-full flex items-center justify-center cursor-pointer transition-all duration-250"
                style={{
                  background: 'radial-gradient(circle at 35% 35%, #5A7BFF 0%, #4F6BFF 40%, #6E5CFF 100%)',
                  boxShadow: '0 0 40px 10px rgba(79,107,255,0.35), 0 0 80px 20px rgba(110,92,255,0.2)',
                  animation: isPlaying ? 'orbPulse 3s ease-in-out infinite' : 'none',
                }}
                onClick={handleOrbClick}
                onMouseEnter={e => { e.currentTarget.style.transform = 'scale(1.05)'; }}
                onMouseLeave={e => { e.currentTarget.style.transform = 'scale(1)'; }}
              >
                {/* Sound waves */}
                <div className="flex items-center gap-1">
                  {[3, 6, 9, 12, 9, 6, 3]?.map((h, i) => (
                    <div
                      key={i}
                      className="w-1 rounded-full"
                      style={{
                        height: `${h * 2}px`,
                        background: 'rgba(255,255,255,0.9)',
                        animation: isPlaying ? `soundBar${i} 1.2s ease-in-out infinite` : 'none',
                        animationDelay: `${i * 0.1}s`,
                      }}
                    />
                  ))}
                </div>
                <style>{`
                  ${[0,1,2,3,4,5,6]?.map(i => `
                    @keyframes soundBar${i} {
                      0%, 100% { transform: scaleY(0.4); opacity: 0.6; }
                      50% { transform: scaleY(1); opacity: 1; }
                    }
                  `)?.join('')}
                `}</style>
              </div>
              {/* Dot indicators */}
              {[0, 60, 120, 180, 240, 300]?.map((deg, i) => (
                <div
                  key={i}
                  className="absolute w-2 h-2 rounded-full"
                  style={{
                    background: i % 2 === 0 ? '#4F6BFF' : '#6E5CFF',
                    top: `calc(50% + ${Math.sin(deg * Math.PI / 180) * 100}px - 4px)`,
                    left: `calc(50% + ${Math.cos(deg * Math.PI / 180) * 100}px - 4px)`,
                    opacity: 0.6,
                  }}
                />
              ))}
            </div>
          </div>

          {/* Right Cards */}
          <div className="w-full lg:w-[30%] flex flex-col gap-4">
            {rightCards?.map(uc => (
              <UseCaseCard key={uc?.id} useCase={uc} isActive={activeCard === uc?.id} isPlaying={isPlaying} onClick={handleCardClick} />
            ))}
          </div>
        </div>
        <audio ref={audioRef} preload="none" />
      </div>
    </section>
  );
};

export default AudioDemoSection;
